import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { useSession } from '@/lib/platform/auth/client';
import { useNotifications } from '@/components/platform/notifications';
import { useEffect, useState, useCallback } from 'react';
import { logger } from '@/lib/platform/utils/logger';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queries/query-keys';
import { fetchWithBypass } from '@/lib/platform/utils/fetch';
import { useOrganizationSelection } from '@/lib/platform/hooks/use-organization-preferences';

interface PendingInvitation {
  id: string;
  organizationId: string;
  organizationName: string;
  organizationLogo?: string | null;
  role: string;
  inviterName: string;
}

export function useInvitationPolling() {
  const { data: session } = useSession();
  const userEmail = session?.user?.email;
  const { refreshOrganization } = useOrganization();
  const { showInvitation } = useNotifications();
  const [shownInvitations, setShownInvitations] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();
  const { setSelectedOrganization } = useOrganizationSelection();

  const handleAcceptInvitation = useCallback(
    async (invitation: PendingInvitation) => {
      try {
        logger.log('[InvitationPolling] Accepting invitation:', invitation.id);

        // Use our custom PATCH endpoint to accept invitation with multi-role support
        const response = await fetchWithBypass('/api/invitations', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            invitationId: invitation.id,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to accept invitation');
        }

        const result = await response.json();
        logger.log('[InvitationPolling] Accept result:', result);

        // Store the organization ID so OrganizationProvider can select it
        setSelectedOrganization(invitation.organizationId);

        // Refresh organization context to show new organization
        await refreshOrganization();

        // Invalidate clubs query to refresh organization list in sidebar
        queryClient.invalidateQueries({ queryKey: queryKeys.clubs.all });

        logger.log(`[InvitationPolling] Successfully joined ${invitation.organizationName}`);

        // Remove from shown set
        setShownInvitations((prev) => {
          const newSet = new Set(prev);
          newSet.delete(invitation.id);
          return newSet;
        });
      } catch (error: unknown) {
        logger.error('[InvitationPolling] Error accepting invitation:', error);
        throw error; // Re-throw so toast can show error state
      }
    },
    [setSelectedOrganization, refreshOrganization, queryClient]
  );

  const checkForInvitations = useCallback(async () => {
    if (!userEmail) return;

    try {
      const res = await fetchWithBypass(
        `/api/invitations/pending?email=${encodeURIComponent(userEmail)}`
      );

      if (!res.ok) return;

      const { invitations } = await res.json();

      invitations.forEach((invitation: PendingInvitation) => {
        // Don't show toast if we've already shown it for this invitation
        if (shownInvitations.has(invitation.id)) return;

        // Mark as shown
        setShownInvitations((prev) => new Set(prev).add(invitation.id));

        // Format roles for display
        const roleLabels: Record<string, string> = {
          admin: 'Admin',
          dashboard: 'Dashboard',
          analytics: 'Reports',
          cfo: 'Sales',
          transfer_manager: 'Transfer',
          marketing_manager: 'Marketing',
          sports_director: 'Brand',
          user_management: 'Users',
          finance_manager: 'Billing',
          settings: 'Settings',
          plugins: 'Plugins',
          log: 'Audit Log',
          api: 'API',
          approver: 'Approver',
        };

        const roles = invitation.role.split(',').map((r) => r.trim());
        const roleDisplay = roles.map((r) => roleLabels[r] || r).join(', ');

        // Show custom toast notification
        showInvitation({
          invitationId: invitation.id,
          organizationId: invitation.organizationId,
          organizationName: invitation.organizationName,
          organizationLogo: invitation.organizationLogo,
          roles: roleDisplay,
          onAccept: async () => {
            await handleAcceptInvitation(invitation);
          },
          onDecline: () => {
            // Remove from shown set so it can be shown again if user declines
            setShownInvitations((prev) => {
              const newSet = new Set(prev);
              newSet.delete(invitation.id);
              return newSet;
            });
          },
        });
      });
    } catch (error) {
      logger.error('[InvitationPolling] Error checking invitations:', error);
    }
  }, [userEmail, shownInvitations, showInvitation, handleAcceptInvitation]);

  useEffect(() => {
    if (!userEmail) return;

    // Check immediately on mount
    checkForInvitations();

    // Then check every 30 seconds
    const interval = setInterval(checkForInvitations, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]); // checkForInvitations removed to prevent infinite loop

  return null;
}
